import { resolveCountedWeightPounds } from "@CommonUtils/pog/productWeight";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import type { AttributeCandidate } from "../domain/spineInference";
import { sizeFamilyOf } from "../domain/sizeFamily";
import {
  isPlaceholderValue,
  type StudioItem,
  type StudioPlacement,
  type StudioRunGeometry,
} from "../domain/studioTypes";

export const POG_ATTRIBUTE_CANDIDATES: readonly AttributeCandidate[] = [
  { attr: "subCategory", label: "Subcategory" },
  { attr: "brand", label: "Brand" },
  { attr: "cdt1", label: "CDT 1" },
  { attr: "cdt2", label: "CDT 2" },
  { attr: "cdt3", label: "CDT 3" },
  { attr: "variant", label: "Variant" },
  { attr: "size", label: "Size" },
];

export const POG_PREFERRED_ORDER = POG_ATTRIBUTE_CANDIDATES.map((candidate) => candidate.attr);

const POG_AUTHORED_ATTRS: ReadonlySet<string> = new Set(POG_PREFERRED_ORDER);

/** Every spelling of absence collapses to ONE bucket; left distinct they fabricate phantom level splits that hide real order violations. */
const DEFAULT_PLACEHOLDER_SPELLINGS: readonly string[] = [
  "na",
  "n/a",
  "n-a",
  "none",
  "unknown",
  "unspecified",
];

const attrValue = (
  value: string | null | undefined,
  placeholderSpellings: readonly string[] | false,
): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  if (placeholderSpellings === false) return trimmed;
  const placeholders = new Set(
    placeholderSpellings.map((spelling) => spelling.trim().toLowerCase()),
  );
  return placeholders.has(trimmed.toLowerCase()) ? "Unspecified" : trimmed;
};

export type PogQuantityUnit = "fl oz" | "l" | "ml" | "oz" | "lb" | "loads" | "ct";

const DEFAULT_NAME_UNIT_PREFERENCE: readonly PogQuantityUnit[] = [
  "fl oz",
  "l",
  "ml",
  "oz",
  "lb",
  "loads",
  "ct",
];

const DEFAULT_UNIT_ALIASES: Readonly<Record<string, PogQuantityUnit>> = {
  "fl oz": "fl oz",
  "fl. oz": "fl oz",
  "fluid ounce": "fl oz",
  "fluid ounces": "fl oz",
  oz: "oz",
  ounce: "oz",
  ounces: "oz",
  lb: "lb",
  lbs: "lb",
  pound: "lb",
  pounds: "lb",
  ml: "ml",
  l: "l",
  liter: "l",
  liters: "l",
  litre: "l",
  litres: "l",
  load: "loads",
  loads: "loads",
  ct: "ct",
  count: "ct",
  pac: "ct",
  pacs: "ct",
  pod: "ct",
  pods: "ct",
};

/** Stated weights only — a pack-name guess does not qualify. */
const statedWeightKg = (item: {
  name?: string;
  size?: string;
  unitWeight?: number | null;
  trayWeight?: number | null;
  caseWeight?: number | null;
  weightUom?: string | null;
  noOfUnitsInTray?: number | null;
  noOfUnitsInCase?: number | null;
}) => {
  const resolved = resolveCountedWeightPounds(
    { ...item, name: item.name ?? "", size: item.size ?? "" },
    "UNIT",
  );
  return resolved === null || resolved.provenance !== "measured"
    ? undefined
    : +(resolved.pounds * KG_PER_POUND).toFixed(3);
};

const KG_PER_POUND = 0.45359237;

const regexEscape = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const quantityTokensOf = (
  name: string,
  aliases: Readonly<Record<string, PogQuantityUnit>>,
): { magnitude: number; unit: PogQuantityUnit }[] => {
  const normalizedAliases = Object.fromEntries(
    Object.entries(aliases).map(([source, unit]) => [
      source.toLowerCase().replace(/\s+/g, " "),
      unit,
    ]),
  );
  const spellings = Object.keys(normalizedAliases)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  if (spellings.length === 0) return [];
  const pattern = new RegExp(
    `(\\d+(?:\\.\\d+)?)\\s*(${spellings.map(regexEscape).join("|")})(?![a-z])`,
    "gi",
  );
  return [...name.matchAll(pattern)].flatMap((match) => {
    const unit = normalizedAliases[match[2].toLowerCase().replace(/\s+/g, " ")];
    return unit ? [{ magnitude: parseFloat(match[1]), unit }] : [];
  });
};

/** Liquid ounces are fluid; a powder "55 oz" stays mass. */
const DEFAULT_LIQUID_NAME_TERMS: readonly string[] = ["liquid", "fl oz", "fluid ounce"];

const searchableTerm = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]+/g, "");

const nameMatchesAnyTerm = (name: string, terms: readonly string[] | false): boolean => {
  if (terms === false) return false;
  const searchableName = searchableTerm(name);
  return terms.some((term) => {
    const searchable = searchableTerm(term);
    return searchable.length > 0 && searchableName.includes(searchable);
  });
};

const enrichQuantityUnit = (
  quantity: string | null,
  name: string | null | undefined,
  liquidNameTerms: readonly string[] | false,
  unitPreference: readonly PogQuantityUnit[] | false,
  unitAliases: Readonly<Record<string, PogQuantityUnit>>,
  zeroIsMissing: boolean,
  liquidOuncesAreFluid: boolean,
): string | null => {
  const text = String(name ?? "");
  const liquid = nameMatchesAnyTerm(text, liquidNameTerms);
  const tokens: { magnitude: number; unit: PogQuantityUnit }[] = [];
  for (const token of quantityTokensOf(text, unitAliases)) {
    tokens.push({
      magnitude: token.magnitude,
      unit: token.unit === "oz" && liquid && liquidOuncesAreFluid ? "fl oz" : token.unit,
    });
  }
  const columnMagnitude = quantity
    ? parseFloat(quantity.match(/-?\d+(?:\.\d+)?/)?.[0] ?? "0") || 0
    : 0;
  if ((!quantity || (zeroIsMissing && columnMagnitude === 0)) && unitPreference !== false) {
    for (const preferred of unitPreference || []) {
      const token = tokens.find((entry) => entry.unit === preferred && entry.magnitude > 0);
      if (token) return `${token.magnitude} ${token.unit}`;
    }
    return quantity;
  }
  if (!/^\d+(\.\d+)?$/.test(quantity!)) return quantity;
  // Never append "loads" — a liquid's load count often equals its fl-oz number.
  for (const preferred of unitPreference || []) {
    if (preferred === "loads") continue;
    const token = tokens.find(
      (entry) => entry.unit === preferred && Math.abs(entry.magnitude - columnMagnitude) < 0.05,
    );
    if (token) return `${quantity} ${token.unit}`;
  }
  return quantity;
};

const SIZE_TOKEN = /-?\d+(?:\.\d+)?/;

const reconcileQuantityLabels = (
  list: StudioItem[],
  quantityAttribute: string,
  blockAttributes: readonly string[],
  minimumFamilyEvidence: number,
  reconciliationRange: { minimum: number; maximum: number },
  liquidOuncesAreFluid: boolean,
): void => {
  const byBlock = new Map<string, StudioItem[]>();
  for (const item of list) {
    const key = blockAttributes.map((attr) => item.attrs[attr] ?? "").join("␟");
    const members = byBlock.get(key) ?? [];
    members.push(item);
    byBlock.set(key, members);
  }
  byBlock.forEach((members) => {
    const parsed = members
      .filter((item) => item.attrs[quantityAttribute])
      .map((item) => ({ item, ...sizeFamilyOf(String(item.attrs[quantityAttribute])) }));
    const volume = parsed.filter((entry) => entry.family === "volume");
    if (volume.length < minimumFamilyEvidence) return;
    const magnitudes = volume.map((entry) => entry.magnitude);
    // Tight floor, loose ceiling — a pod count next to fl-oz is below the volume range.
    const low = Math.min(...magnitudes) * reconciliationRange.minimum;
    const high = Math.max(...magnitudes) * reconciliationRange.maximum;
    for (const entry of parsed) {
      if (entry.family !== "bare" && !(liquidOuncesAreFluid && entry.family === "oz")) continue;
      if (entry.magnitude < low || entry.magnitude > high) continue;
      const number = String(entry.item.attrs[quantityAttribute]).match(SIZE_TOKEN)?.[0];
      if (!number) continue;
      entry.item.attrs = { ...entry.item.attrs, [quantityAttribute]: `${number} fl oz` };
    }
  });
};

export interface StudioPogData {
  items: StudioItem[];
  runs: StudioRunGeometry[];
  candidates: AttributeCandidate[];
}

export interface PogNormalizationOptions {
  placeholderSpellings?: readonly string[] | false;
  quantityAttribute?: string | false;
  nameBasedUnitInference?: boolean;
  liquidNameTerms?: readonly string[] | false;
  unitPreference?: readonly PogQuantityUnit[] | false;
  unitAliases?: Readonly<Record<string, PogQuantityUnit>>;
  zeroIsMissing?: boolean;
  liquidOuncesAreFluid?: boolean;
  minimumFamilyEvidence?: number;
  reconciliationRange?: { minimum: number; maximum: number };
  reconciliationBlockAttributes?: readonly string[] | false;
}

export interface PogSectionSemantics {
  sections: readonly string[];
  when?: Readonly<Record<string, readonly string[]>>;
  drop?: readonly string[];
  columns: Readonly<Record<string, string>>;
}

export const DEFAULT_SECTION_ATTR = "variant";

/** A column name is not an attribute; empty source writes nothing (absent ≠ blank). */
const applySectionSemantics = (
  attrs: Record<string, string | null>,
  mappingOf:
    | ((
        section: string | null,
        attrs: Readonly<Record<string, string | null>>,
      ) => ResolvedSectionMapping | undefined)
    | undefined,
  sectionAttr: string,
): ReadonlyMap<string, string> => {
  /** semantic attribute written → the live column it was read from. */
  const written = new Map<string, string>();
  const mapping = mappingOf?.(attrs[sectionAttr] ?? null, attrs);
  if (!mapping) return written;
  for (const [column, semantic] of Object.entries(mapping.columns)) {
    const value = attrs[column];
    if (value === undefined || value === null) continue;
    attrs[semantic] = value;
    written.set(semantic, column);
  }
  // Clear consumed columns after the whole mapping — a source must not be wiped mid-pass.
  for (const [column, semantic] of Object.entries(mapping.columns)) {
    if (semantic !== column && !written.has(column)) attrs[column] = null;
  }
  for (const column of mapping.drop) attrs[column] = null;
  return written;
};

interface ResolvedSectionMapping {
  drop: readonly string[];
  columns: Readonly<Record<string, string>>;
}

const sectionSemanticsResolver = (
  semantics: readonly PogSectionSemantics[] | undefined,
):
  | ((
      section: string | null,
      attrs: Readonly<Record<string, string | null>>,
    ) => ResolvedSectionMapping | undefined)
  | undefined => {
  if (!semantics || semantics.length === 0) return undefined;
  const normalized = semantics.map((mapping) => ({
    sections: new Set(mapping.sections.map((section) => section.trim().toLowerCase())),
    drop: mapping.drop ?? [],
    when: Object.entries(mapping.when ?? {}).map(
      ([column, values]) =>
        [column, new Set(values.map((value) => value.trim().toLowerCase()))] as const,
    ),
    columns: mapping.columns,
  }));
  return (section, attrs) => {
    const key = section?.trim().toLowerCase() ?? "";
    let fallback: ResolvedSectionMapping | undefined;
    for (const mapping of normalized) {
      if (mapping.sections.size === 0) {
        fallback ??= mapping;
        continue;
      }
      if (!mapping.sections.has(key)) continue;
      const matches = mapping.when.every(([column, values]) =>
        values.has(
          String(attrs[column] ?? "")
            .trim()
            .toLowerCase(),
        ),
      );
      if (matches) return mapping;
    }
    return fallback;
  };
};

export interface PogAdapterOptions {
  itemIdentifier?: "productCode" | "upc" | "itemId";
  itemIdentifierByProductCode?: Readonly<Record<string, string>>;
  attributeOverlay?: Readonly<Record<string, Readonly<Record<string, string | null>>>>;
  overlayCandidates?: readonly AttributeCandidate[];
  /** Truncated POG names split unit families the data does not have. */
  nameOverlay?: Readonly<Record<string, string | null>>;
  normalization?: PogNormalizationOptions | false;
  sectionSemantics?: readonly PogSectionSemantics[];
  sectionAttr?: string;
}

export const itemMasterIdentifierOf = (
  item: PlanogramItem,
  strategy: NonNullable<PogAdapterOptions["itemIdentifier"]> = "productCode",
): string => {
  if (strategy === "upc") return item.upc;
  if (strategy === "itemId") return item.id;
  return item.productCode;
};

export interface PogProductReference {
  productCode: string;
  itemId: string;
  upc: string;
  name: string;
  sourceValues: Readonly<Record<string, string | null>>;
  conflictingSourceFields?: readonly string[];
}

const sourceValue = (value: unknown): string | null => {
  const text = String(value ?? "").trim();
  return text || null;
};

export const pogProductIdentifierOf = (
  product: PogProductReference,
  strategy: NonNullable<PogAdapterOptions["itemIdentifier"]> = "productCode",
): string => {
  if (strategy === "upc") return product.upc;
  if (strategy === "itemId") return product.itemId;
  return product.productCode;
};

export const pogProductReferencesOf = (pogs: readonly Pog[]): PogProductReference[] => {
  const products = new Map<string, PogProductReference>();
  const mergeValue = (left: string | null, right: string | null): string | null => {
    if (!left) return right;
    if (!right) return left;
    return left === right ? left : null;
  };
  pogs.forEach((pog) =>
    pog.planogram.bays.forEach((bay) =>
      bay.shelves.forEach((shelf) =>
        shelf.items.forEach((item) => {
          if (!(item instanceof PlanogramItem)) return;
          const productCode = item.productCode || item.id || item.upc;
          const next: PogProductReference = {
            productCode,
            itemId: item.id,
            upc: item.upc,
            name: item.name,
            sourceValues: {
              brand: sourceValue(item.brand),
              variant: sourceValue(item.variant),
              categoryCode: sourceValue(item.categoryCode),
              subCategory: sourceValue(item.subCategory),
              size: sourceValue(item.size),
              color: sourceValue(item.color),
              cdt1: sourceValue(item.cdt1),
              cdt2: sourceValue(item.cdt2),
              cdt3: sourceValue(item.cdt3),
            },
          };
          const existing = products.get(productCode);
          if (!existing) {
            products.set(productCode, next);
            return;
          }
          const conflictingSourceFields = new Set(existing.conflictingSourceFields ?? []);
          const sourceValues = Object.fromEntries(
            Object.keys(next.sourceValues).map((key) => {
              if (conflictingSourceFields.has(key)) return [key, null];
              const left = existing.sourceValues[key] ?? null;
              const right = next.sourceValues[key] ?? null;
              if (left && right && left !== right) {
                conflictingSourceFields.add(key);
                return [key, null];
              }
              return [key, mergeValue(left, right)];
            }),
          );
          products.set(productCode, {
            ...existing,
            itemId: mergeValue(existing.itemId || null, next.itemId || null) ?? "",
            upc: mergeValue(existing.upc || null, next.upc || null) ?? "",
            sourceValues,
            conflictingSourceFields: [...conflictingSourceFields],
          });
        }),
      ),
    ),
  );
  return [...products.values()];
};

interface ResolvedNormalizationOptions {
  placeholderSpellings: readonly string[] | false;
  quantityAttribute: string | false;
  nameBasedUnitInference: boolean;
  liquidNameTerms: readonly string[] | false;
  unitPreference: readonly PogQuantityUnit[] | false;
  unitAliases: Readonly<Record<string, PogQuantityUnit>>;
  zeroIsMissing: boolean;
  liquidOuncesAreFluid: boolean;
  minimumFamilyEvidence: number;
  reconciliationRange: { minimum: number; maximum: number };
  reconciliationBlockAttributes: readonly string[] | false;
}

/* Both namespaces — a missing attr is ""; dropping either widens the block. */
const DEFAULT_RECONCILIATION_BLOCK_ATTRIBUTES: readonly string[] = [
  "variant",
  "subCategory",
  "brand",
  "form",
  "packType",
  "cdt1",
  "cdt2",
  "cdt3",
];

const resolveNormalization = (
  normalization: PogAdapterOptions["normalization"],
): ResolvedNormalizationOptions => {
  if (normalization === false) {
    return {
      placeholderSpellings: false,
      quantityAttribute: false,
      nameBasedUnitInference: false,
      liquidNameTerms: false,
      unitPreference: false,
      unitAliases: {},
      zeroIsMissing: false,
      liquidOuncesAreFluid: false,
      minimumFamilyEvidence: 2,
      reconciliationRange: { minimum: 0.8, maximum: 2 },
      reconciliationBlockAttributes: false,
    };
  }
  return {
    placeholderSpellings: normalization?.placeholderSpellings ?? DEFAULT_PLACEHOLDER_SPELLINGS,
    quantityAttribute: normalization?.quantityAttribute ?? "size",
    nameBasedUnitInference: normalization?.nameBasedUnitInference ?? true,
    liquidNameTerms: normalization?.liquidNameTerms ?? DEFAULT_LIQUID_NAME_TERMS,
    unitPreference: normalization?.unitPreference ?? DEFAULT_NAME_UNIT_PREFERENCE,
    unitAliases: normalization?.unitAliases ?? DEFAULT_UNIT_ALIASES,
    zeroIsMissing: normalization?.zeroIsMissing ?? true,
    liquidOuncesAreFluid: normalization?.liquidOuncesAreFluid ?? true,
    minimumFamilyEvidence: normalization?.minimumFamilyEvidence ?? 2,
    reconciliationRange: normalization?.reconciliationRange ?? { minimum: 0.8, maximum: 2 },
    reconciliationBlockAttributes:
      normalization?.reconciliationBlockAttributes ?? DEFAULT_RECONCILIATION_BLOCK_ATTRIBUTES,
  };
};

export const pogToStudioItems = (
  pogs: readonly Pog[],
  options: PogAdapterOptions = {},
): StudioPogData => {
  const items = new Map<string, StudioItem>();
  const runs: StudioRunGeometry[] = [];
  const normalization = resolveNormalization(options.normalization);
  const mappingOf = sectionSemanticsResolver(options.sectionSemantics);
  const sectionAttr = options.sectionAttr ?? DEFAULT_SECTION_ATTR;

  pogs.forEach((pog, runIndex) => {
    const bays = pog.planogram.bays;
    const firstBay = bays[0];
    // Live POGs can arrive as cm × 100; engine geometry is centimeters.
    const rawBayWidth = firstBay?.width ?? 0;
    const scale = rawBayWidth > 1000 ? 0.01 : 1;
    runs.push({
      run: runIndex,
      name: `Aisle ${runIndex + 1}`,
      widthCm: bays.reduce((sum, bay) => sum + bay.width, 0) * scale,
      heightCm: pog.planogram.getMaxHeight() * scale,
      bayWidthCm: rawBayWidth * scale,
      bays: bays.length,
      shelves: (firstBay?.shelves ?? []).map((shelf, index) => ({
        rank: index + 1,
        yCm: shelf.getYBottom() * scale,
        thicknessCm: shelf.thickness * scale,
      })),
    });

    bays.forEach((bay, bayIndex) => {
      bay.shelves.forEach((shelf, shelfIndex) => {
        for (const item of shelf.items) {
          if (!(item instanceof PlanogramItem)) continue;
          const coordinates = item.getCoordinates();
          const hFacings = item.getHorizontalFacings();
          const placement: StudioPlacement = {
            run: runIndex,
            shelf: shelfIndex + 1,
            // The authored bay: `getCoordinates()` lays a merged run out as one
            // lane, so an x alone can put this item in the neighbouring bay.
            bay: bayIndex + 1,
            xCm: coordinates.xLeft * scale,
            widthCm: (coordinates.xRight - coordinates.xLeft) * scale,
            hFacings,
          };
          let dosDays: number | undefined;
          let dosPerFacing: number | undefined;
          try {
            dosDays = item.getDaysOfSupply();
            if (Number.isFinite(dosDays) && item.getHorizontalFacings() > 0) {
              dosPerFacing = dosDays / item.getHorizontalFacings();
            } else {
              dosDays = undefined;
            }
          } catch {
            dosDays = undefined;
          }
          const skuId = item.productCode || item.id;
          const itemMasterId =
            options.itemIdentifierByProductCode?.[skuId] ??
            itemMasterIdentifierOf(item, options.itemIdentifier);
          const existing = items.get(skuId);
          if (existing) {
            items.set(skuId, {
              ...existing,
              facings: existing.facings + hFacings,
              placements: [...existing.placements, placement],
            });
          } else {
            const overlayRaw = options.attributeOverlay?.[itemMasterId];
            const overlay = overlayRaw
              ? Object.fromEntries(
                  Object.entries(overlayRaw).map(([attr, value]) => [
                    attr,
                    attrValue(value, normalization.placeholderSpellings),
                  ]),
                )
              : undefined;
            const pogAttrs: Record<string, string | null> = {
              subCategory: attrValue(item.subCategory, normalization.placeholderSpellings),
              brand: attrValue(item.brand, normalization.placeholderSpellings),
              cdt1: attrValue(item.cdt1, normalization.placeholderSpellings),
              cdt2: attrValue(item.cdt2, normalization.placeholderSpellings),
              cdt3: attrValue(item.cdt3, normalization.placeholderSpellings),
              variant: attrValue(item.variant, normalization.placeholderSpellings),
              size: attrValue(item.size, normalization.placeholderSpellings),
            };
            const quantityAttribute = normalization.quantityAttribute;
            const enrichQuantityOf = (source: Record<string, string | null>) => {
              if (!quantityAttribute || !normalization.nameBasedUnitInference) return;
              source[quantityAttribute] = enrichQuantityUnit(
                source[quantityAttribute] ?? null,
                options.nameOverlay?.[itemMasterId] ?? item.name,
                normalization.liquidNameTerms,
                normalization.unitPreference,
                normalization.unitAliases,
                normalization.zeroIsMissing,
                normalization.liquidOuncesAreFluid,
              );
            };
            const quantityIsPogAuthored =
              !!quantityAttribute && POG_AUTHORED_ATTRS.has(quantityAttribute);
            if (quantityIsPogAuthored) enrichQuantityOf(pogAttrs);
            const attrs: Record<string, string | null> = { ...pogAttrs, ...overlay };
            if (!quantityIsPogAuthored) enrichQuantityOf(attrs);
            // Semantics last so overlay wins first. Re-enrich only digits — a band label is not missing size.
            const semanticAttrs = applySectionSemantics(attrs, mappingOf, sectionAttr);
            if (
              quantityAttribute &&
              semanticAttrs.has(quantityAttribute) &&
              /\d/.test(String(attrs[quantityAttribute] ?? ""))
            ) {
              enrichQuantityOf(attrs);
            }
            /* Provenance: which keys the item master supplied — its own
               columns in their own order, plus any semantic attribute a
               section mapping filled FROM one of those columns. */
            const overlayKeys = overlayRaw ? Object.keys(overlayRaw) : null;
            const overlayAttrs = overlayKeys
              ? [
                  ...overlayKeys,
                  ...[...semanticAttrs]
                    .filter(
                      ([semantic, source]) =>
                        overlayKeys.includes(source) && !overlayKeys.includes(semantic),
                    )
                    .map(([semantic]) => semantic),
                ]
              : null;
            items.set(skuId, {
              skuId,
              itemMasterId,
              upc: item.upc,
              name: options.nameOverlay?.[itemMasterId] ?? item.name,
              run: runIndex,
              facingWidthCm: item.getWidth() * scale,
              heightCm: item.getHeight() * scale,
              facings: hFacings,
              dosDays,
              dosPerFacing,
              maxFacings: item.maxFacings > 0 ? item.maxFacings : undefined,
              salesAmount: Number.isFinite(item.salesAmount) ? item.salesAmount : undefined,
              // Undefined when unstated so heavy-low rules stay inert.
              weightKg: statedWeightKg(item),
              attrs,
              ...(overlayAttrs ? { overlayAttrs } : {}),
              placements: [placement],
            });
          }
        }
      });
    });
  });

  // All-placeholder columns carry no level.
  const list = [...items.values()];
  if (normalization.quantityAttribute && normalization.reconciliationBlockAttributes !== false) {
    reconcileQuantityLabels(
      list,
      normalization.quantityAttribute,
      normalization.reconciliationBlockAttributes,
      normalization.minimumFamilyEvidence,
      normalization.reconciliationRange,
      normalization.liquidOuncesAreFluid,
    );
  }
  const pool = options.overlayCandidates ?? POG_ATTRIBUTE_CANDIDATES;
  const candidates = pool.filter((candidate) =>
    list.some((item) => !isPlaceholderValue(item.attrs[candidate.attr])),
  );

  return { items: list, runs, candidates: [...candidates] };
};
